/**
 * Notification admin shell — login gate + sidebar nav + feature pages.
 * Same design tokens/components as frontend/web (copied 1:1, plan §9).
 */
import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { logout, sessionUser, tokenStore, type SessionUser } from '@/lib/auth';
import { LoginPage } from '@/features/auth/LoginPage';
import { AdminShell, type AdminPage } from '@/components/layout/AdminShell';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { DeliveryLogPage } from '@/features/delivery-log/DeliveryLogPage';
import { SenderProfilesPage } from '@/features/sender-profiles/SenderProfilesPage';
import { TemplatesPage } from '@/features/templates/TemplatesPage';
import { ComposePage } from '@/features/compose/ComposePage';
import { JobSchedulerPage } from '@/features/job-scheduler/JobSchedulerPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

export default function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [page, setPage] = useState<AdminPage>('dashboard');

  useEffect(() => apiClient.onAuthExpired(() => setUser(null)), []);
  useEffect(() => {
    // Survive a reload: refresh token in sessionStorage → silent re-login.
    const existing = sessionUser.get();
    if (existing) {
      tokenStore
        .refresh()
        .then(() => setUser(existing))
        .catch(() => logout());
    }
  }, []);

  if (!user) {
    return (
      <QueryClientProvider client={queryClient}>
        <LoginPage onLoggedIn={setUser} />
        <Toaster richColors position="top-right" />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AdminShell
        page={page}
        onNavigate={setPage}
        userName={user.name ?? 'Admin'}
        onLogout={() => {
          logout();
          setUser(null);
        }}
      >
        {page === 'dashboard' && <DashboardPage />}
        {page === 'delivery-log' && <DeliveryLogPage />}
        {page === 'sender-profiles' && <SenderProfilesPage />}
        {page === 'templates' && <TemplatesPage />}
        {page === 'compose' && <ComposePage />}
        {page === 'job-scheduler' && <JobSchedulerPage />}
      </AdminShell>
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  );
}
